import { useEffect, useRef } from 'react';
import { useIsHandheld } from './useMediaQuery';

/**
 * GOOGLE ONE TAP — sign a returning visitor in without sending them anywhere.
 *
 * A returning creator currently pays a full redirect round trip to get back into their own studio. One Tap
 * replaces that with a prompt Google renders itself, already knowing who they are: one tap, no page leaves
 * the screen. It is purely a faster DOOR — everything behind it (the staging allowlist, profile creation, the
 * login audit) is the same pipeline the button uses, because a second way in that skipped any of that would
 * be a hole rather than a feature.
 *
 * FOUR REASONS IT STAYS QUIET, and each is deliberate:
 *   - **No client id configured** → nothing happens at all. One Tap needs a Google OAuth WEB client id, which
 *     is separate from the Firebase web config, so a developer without it should get today's behaviour rather
 *     than a console full of errors.
 *   - **Already signed in** → there is nobody to prompt.
 *   - **On a handheld** → the device wall intercepts phone sign-in on purpose (RF3-DEVICE), so offering a
 *     one-tap sign-in and then explaining they should use a bigger screen is worse than not offering it.
 *   - **Dismissed before** → Google's own cooldown handles this, and we do not fight it. A prompt that keeps
 *     reappearing after it was closed is the single most complained-about thing about One Tap.
 *
 * The client id is NOT a secret — OAuth client ids are public by design and ship in every bundle that uses
 * them — so it belongs in `REACT_APP_GOOGLE_CLIENT_ID` alongside the rest of the Firebase web config, not in
 * Secret Manager.
 */

/** The slice of Google Identity Services we use. Declared locally so we take no dependency for one prompt. */
interface GsiCredentialResponse { credential?: string }
interface GsiIdApi {
  initialize: (config: {
    client_id: string;
    callback: (res: GsiCredentialResponse) => void;
    auto_select?: boolean;
    cancel_on_tap_outside?: boolean;
    context?: 'signin' | 'signup' | 'use';
  }) => void;
  prompt: () => void;
  cancel: () => void;
}
type GsiWindow = Window & { google?: { accounts?: { id?: GsiIdApi } } };

const GSI_SRC = 'https://accounts.google.com/gsi/client';

/** Load the Google script once per page, however many hooks ask for it. */
function loadGsi(): Promise<GsiIdApi | null> {
  return new Promise((resolve) => {
    const w = window as GsiWindow;
    if (w.google?.accounts?.id) { resolve(w.google.accounts.id); return; }

    const existing = document.querySelector<HTMLScriptElement>(`script[src="${GSI_SRC}"]`);
    const onReady = () => resolve((window as GsiWindow).google?.accounts?.id || null);
    if (existing) { existing.addEventListener('load', onReady, { once: true }); return; }

    const script = document.createElement('script');
    script.src = GSI_SRC;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', onReady, { once: true });
    // A blocked or failed script must be silent: One Tap is an accelerant, and the ordinary sign-in button is
    // still sitting on the page underneath it.
    script.addEventListener('error', () => resolve(null), { once: true });
    document.head.appendChild(script);
  });
}

export interface GoogleOneTapOptions {
  /** False whenever a prompt would be wrong — signed in, still loading, or a modal already has the screen. */
  enabled: boolean;
  /** Hand back the Google ID token; the caller exchanges it through the normal sign-in pipeline. */
  onCredential: (idToken: string) => void;
}

export function useGoogleOneTap({ enabled, onCredential }: GoogleOneTapOptions): void {
  const isHandheld = useIsHandheld();
  // The callback is held in a ref so a re-render with a new closure never re-initializes Google — which would
  // re-prompt somebody who just dismissed it.
  const cb = useRef(onCredential);
  cb.current = onCredential;
  const started = useRef(false);

  useEffect(() => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!enabled || isHandheld || !clientId || started.current) return;
    started.current = true;

    let cancelled = false;
    void loadGsi().then((id) => {
      if (!id || cancelled) return;
      id.initialize({
        client_id: clientId,
        callback: (res) => { if (res?.credential) cb.current(res.credential); },
        // Never sign somebody in without them touching anything — a silent auto-login is indistinguishable
        // from a bug to the person it happens to.
        auto_select: false,
        // Tapping outside is a dismissal, and dismissal should be easy.
        cancel_on_tap_outside: true,
        context: 'signin',
      });
      id.prompt();
    });

    return () => {
      cancelled = true;
      (window as GsiWindow).google?.accounts?.id?.cancel();
    };
  }, [enabled, isHandheld]);
}

export default useGoogleOneTap;
